import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import { AuthSkyline } from '../components/common/AuthSkyline';
import { Helmet } from 'react-helmet-async';
import { PencilFill, FunnelFill, InfoCircleFill, XCircleFill, ArrowLeftCircle, ArrowRightCircle } from 'react-bootstrap-icons'; // 1. Import an icon
import {FaTimes} from 'react-icons/fa';
import {
  BannerText,
  BannerIconButton,
  BannerContent,
  BannerSubtext,
  InfoCircleMod,
  SearchBarInput, 
  SearchBarWrapper,
  GhostText,
  TagWrap,
  Tag,
  RemoveTagButton,
  ContentArea, 
  DisplayArea,
  SearchArea,
  GapCover,
  PageSelectArea,
  BottomGapCover,
  SearchPageShell2,
  InfoBanner2,
  ResultsGrid2,
  LoadingOverlay,
  LoadingTopLayer,
  NoResultsMessage,
  Overlay,
  Dialog,
} from '../styles/search/search.style';
import { Link } from 'react-router-dom';
import { SUBJECT_OPTIONS, UserDetails, AvailabilityObject, ITEMS_PER_PAGE} from '../types/schemas/abc';
import { useTutors } from '../hooks/useTutors';
import { TutorCard } from '../components/search/TutorCard';
import { useUser } from '../contextManager/user.context';
import { SimilaritySidebar } from '../components/search/SimilaritySidebar';
import { TutorDetailView } from '../components/search/TutorDetailView';
import { ClipLoader } from 'react-spinners'; // CHANGED: Import ClipLoader
import { useToast } from '../contextManager/toast.context';
import { SubjectFilteringTip, TutorTip } from '../components/register/InfoBlock';
import { calculateExpertiseMatch, calculateScheduleOverlap } from '../utils/searchHelpers';
import { useParents } from '../hooks/useParents';
import { ParentCard } from '../components/search/ParentCard';
import { ParentDetailView } from '../components/search/ParentDetailView';
import { Area } from '../styles/_app.style';
import { NAVIGATION_HEIGHT } from '../constants/global';
import { useScale } from './HeightScaler';
import { createPortal } from 'react-dom';
import { TuteeLoader } from '../components/common/TuteeLoader';
import { BookLoader } from '../components/common/BookLoader';
import { FoldingBookLoader } from '../components/common/FoldingBookLoader';
import { ClosedBookLogo } from '../components/common/ClosedBookLogo';
import { BookLogo } from '../components/common/BookLogo';
import { styled } from '../styles/stitches.config';
import { BookLogoLoader } from '../components/common/BookLogoLoader';
import { UnverifiedAccountOverlay } from '../components/search/UnverifiedAccountOverlay';


const LogoWrapper = styled('div', {
  width: '200px',      // The actual size of the "smallish" loader
  height: 'auto',
  margin: '0 auto',   // Center it
  padding: '$4',      // Give it some breathing room
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

interface SearchPageProps { 
  scrollContainerRef: React.RefObject<HTMLElement | null>;
}

const PADDING_VALUE = '1rem';


const SearchPage: React.FC<SearchPageProps> = ({scrollContainerRef}) => {

    const [isFiltering, setIsFiltering] = useState(false);
    const [subjectInput, setSubjectInput] = useState('');
    const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
    const [isSidebarOverflowing, setIsSidebarOverflowing] = useState(false);

    const scrollPositionRef = useRef(0);

    const [expandedParent, setExpandedParent] = useState<UserDetails | null>(null);
    const [currentPage, setCurrentPage] = useState(1);

    const { userDetails: tutorDetails } = useUser();

    const tutorSubjects = useMemo(() => {
      const fromExpertise = tutorDetails?.expertise ? Object.keys(tutorDetails.expertise) : [];
      const fromSubjects = tutorDetails?.subjects || [];
      // Use expertise keys as source of truth, fall back to subjects array
      return fromExpertise.length > 0 ? fromExpertise : fromSubjects;
  }, [tutorDetails]);

  const allowedSubjectList = useMemo(() => [...tutorSubjects].sort(), [tutorSubjects]);
const allowedSubjectSet  = useMemo(() => new Set(allowedSubjectList), [allowedSubjectList]);

  const subjectsToFetch = useMemo(() => {
    // If the user has actively selected any subjects for filtering,
    // use that list for the API call. Otherwise, use the parent's default subjects.
    return selectedSubjects.length > 0 
        ? selectedSubjects 
        : tutorSubjects;
}, [tutorSubjects, selectedSubjects]);

    const {data: parents, isLoading, isError} = useParents(subjectsToFetch);

    const [hoveredParent, setHoveredParent] = useState<UserDetails | null>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [sidebarTop, setSidebarTop] = useState<number | null>(null);

    const displayAreaRef = useRef<HTMLDivElement | null>(null);
    const [sortedParents, setSortedParents] = useState<UserDetails[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const sidebarFinalState = isSidebarOpen && !isSidebarOverflowing ? 'open' : 'closed';

    const {addToast} = useToast();
    const {scale} = useScale();


    useEffect(() => {
      if (isError) {
        addToast({
          variant: 'error', 
          title: 'Error Fetching Tutors',
          // message: 'There was a problem loading tutor data. Please try again.'
        })
      }

    }, [isError, addToast])

    const filteredParents = useMemo(() => {
      if (!parents) return [];
      
      if (selectedSubjects.length === 0) {
        return parents;
      }
  
      return parents.filter(parent => {
        return selectedSubjects.some(selectedSubject =>
          parent.subjects?.includes(selectedSubject)
        );
      });
    }, [parents, selectedSubjects]);

    useEffect(() => {
      if (!filteredParents || !tutorDetails) return;

      const minLoadingTime = 2000; 
      const startTime = Date.now();
      
      setIsProcessing(true);
    
    
      // Perform the sort after a short delay to allow the UI to update
      const sortTimeout = setTimeout(() => {
        const sorted = [...filteredParents].sort((parentA, parentB) => {
          const matchesA = calculateExpertiseMatch(tutorDetails, parentA);
          const overlapA = calculateScheduleOverlap(tutorDetails, parentA);
          const matchesB = calculateExpertiseMatch(tutorDetails, parentB);
          const overlapB = calculateScheduleOverlap(tutorDetails, parentB);
          if (matchesA !== matchesB) return matchesB - matchesA;
          return overlapB - overlapA;
        });
    
        setSortedParents(sorted);
        const elapsedTime = Date.now() - startTime; 
        const remainingTime = minLoadingTime - elapsedTime; 
        setTimeout(() => {
          setIsProcessing(false);
        }, Math.max(0, remainingTime));

      }, 100);
    
      // Simple cleanup
      return () => clearTimeout(sortTimeout);
    
    }, [filteredParents, tutorDetails]);
    
    const isVerified = !!tutorDetails?.account_verified;

    useEffect(() => {
      if (!isVerified) return;
      if (isProcessing || isLoading) {
        document.body.classList.add('search-loading-active');
      } else {
        document.body.classList.remove('search-loading-active');
      }

      return () => {
        document.body.classList.remove('search-loading-active');
      }
    }, [isProcessing, isLoading, isVerified]);


    const paginatedParents = useMemo(() => {
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      return sortedParents.slice(startIndex, endIndex);
    }, [sortedParents, currentPage]);

    const totalPages = Math.ceil(sortedParents.length / ITEMS_PER_PAGE);

    useEffect(() => {
      if (currentPage > totalPages) setCurrentPage(totalPages);
      if (currentPage < 1) setCurrentPage(1);
    }, [totalPages]); // eslint-disable-line react-hooks/exhaustive-deps
    
    // 3) Helpers
    const canPrev = currentPage > 1;
    const canNext = currentPage < totalPages;
    const displayCurrent = sortedParents.length === 0 ? 0 : currentPage; // show 0 of 1 for empty

    const handleNextPage = () => {
      setCurrentPage(prev => Math.min(prev + 1, totalPages));
    }

    const handlePrevPage = () => {
      setCurrentPage(prev => Math.max(prev - 1, 1));
    }

    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>, parent: UserDetails) => {
      const cardEl = e.currentTarget;
      const container = displayAreaRef.current;
      if (container) {
        const cardRect = cardEl.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const cardTop = cardRect.top / scale;
        const containerTop = containerRect.top / scale;
        const topInside = cardTop - containerTop + container.scrollTop;
        setSidebarTop(topInside);
      }
      setHoveredParent(parent);
      setIsSidebarOpen(true);
    };


    
    const handleMouseLeave = () => {
      setIsSidebarOpen(false); 
    };

    const handleCardClick = (parent: UserDetails) => {
      if (scrollContainerRef.current) { 
        scrollPositionRef.current = scrollContainerRef.current.scrollTop; 
      }
      setExpandedParent(parent);
      setIsSidebarOpen(false);
      setHoveredParent(null);
    };

    useEffect(() => {
      const container = scrollContainerRef.current; 
      if (!container) return; 

      if (expandedParent) {
        container.scrollTo({top: 0, behavior: 'auto'});
      } else {
        container.scrollTo({top: scrollPositionRef.current, behavior: 'auto'})
      }
    }, [expandedParent]);

    useEffect(() => {
      const container = scrollContainerRef.current; 
      if (!container) return; 
      container.scrollTo({top: 0, behavior: 'auto'});


    }, [currentPage])


    // replace your existing `suggestion` useMemo
    const suggestion = useMemo(() =>
      allowedSubjectList
        .filter(opt => !selectedSubjects.includes(opt))
        .find(opt => opt.toLowerCase().startsWith(subjectInput.toLowerCase())) || '',
    [subjectInput, selectedSubjects, allowedSubjectList]);

    const addSubject = (subjectToAdd: string) => {
      if (!subjectToAdd) return;
      if (!allowedSubjectSet.has(subjectToAdd)) {
        addToast({ variant: 'info', title: 'You can only filter by subjects you teach.' });
        setSubjectInput('');
        return;
      }
      if (!selectedSubjects.includes(subjectToAdd)) {
        setSelectedSubjects(prev => [...prev, subjectToAdd]);
      }
      setSubjectInput('');
    };

    const removeSubject = (subjectToRemove: string) => {
      setSelectedSubjects(prev => prev.filter(s => s !== subjectToRemove));
    };
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => { // Changed HTMLInputElement to HTMLDivElement
      if (['Enter','Tab'].includes(e.key) && suggestion) {
        e.preventDefault();
        addSubject(suggestion);
      }
    };
    const handleOpenFilter = () => setIsFiltering(true);
    const handleCloseFilter = () => setIsFiltering(false);
    const showNoResults = !isLoading && !isProcessing && sortedParents.length === 0;
      // detailed view variant was down here. .
      const expandedBottomRem = `calc(100vh / ${scale} - (${NAVIGATION_HEIGHT} + ${PADDING_VALUE} + 1rem))`;
      const nonExpandedBottomRem = `calc(100vh - (${NAVIGATION_HEIGHT} + ${PADDING_VALUE} + 8rem))`;


    const closeProfileModal = () => {
      setExpandedParent(null);
    }

    // ✅ ADD THIS BLOCK
    if (!tutorDetails?.account_verified) {
      return (
        <Area css={{ height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`, marginTop: `calc(${NAVIGATION_HEIGHT})`, position: 'relative' }}>
        <AuthSkyline light />
          <UnverifiedAccountOverlay 
            isProfileComplete={tutorDetails?.isProfileComplete ?? false} 
          />
        </Area>
      );
    }

    return (
      <Area css={{ height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`, marginTop: `calc(${NAVIGATION_HEIGHT})`, position: 'relative' }}>
    <AuthSkyline light />
    <>
      <BottomGapCover/>
              <>
          <GapCover/>
            <Helmet>
              <title>Search Parents - tute</title>
            </Helmet>
            <SearchPageShell2>
            {(isLoading || isProcessing) && (
                <LoadingOverlay>
                  <TuteeLoader />
                  {/* <LogoWrapper>
                    <ClosedBookLogo/>
                  </LogoWrapper> */}
                </LoadingOverlay>
              )}

                {showNoResults && (
                  <NoResultsMessage>
                    <h2>No Parents Found</h2>
                    <p>Add expertise in <Link to="/profile">profile</Link>.</p>
                  </NoResultsMessage>
                )}
            <ContentArea>
            <SearchArea>
              {isFiltering ? (
                  <>
              <SubjectFilteringTip/>
              <InfoBanner2>
              <SearchBarWrapper>
                    {/* The Ghost Text sits behind the input */}
                    {subjectInput && suggestion && (
                      <GhostText>
                        {subjectInput}{suggestion.slice(subjectInput.length)}
                      </GhostText>
                    )}
      
                    {/* The actual, visible input the user types in */}
                    <SearchBarInput
                      type="search"
                      placeholder="Search by subject and press Enter..."
                      value={subjectInput}
                      onChange={(e) => setSubjectInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                      </SearchBarWrapper>
              
                      <BannerIconButton onClick={handleCloseFilter}>
                          <XCircleFill size={18} />
                      </BannerIconButton>
              
                          </InfoBanner2>
                          </>
                      ) : (
                          <>
                    <TutorTip/>
                      <InfoBanner2> 
                          <BannerContent>
                          <BannerText>Showing top matches for you.</BannerText>
                          <BannerSubtext>
                          <InfoCircleMod/> 
                          <span>
                      
                            By default, parents are ordered by how far they match your <Link to="/profile">profile</Link>. Click the icon to filter by subject.
                          </span>
                          
                          </BannerSubtext>
                        </BannerContent>
              
                        <BannerIconButton onClick={handleOpenFilter}>
                          <FunnelFill size={18} />
                        </BannerIconButton>
                        </InfoBanner2>
                          </>
                        ) }

                        {selectedSubjects.length > 0 && (
                          <TagWrap display={true}  style={{justifyContent: 'flex-start'}}>
                            {selectedSubjects.map(subject => (
                              <Tag key={subject}>
                                {subject}
                                <RemoveTagButton onClick={() => removeSubject(subject)}>
                                  <FaTimes size={10} />
                                </RemoveTagButton>
                              </Tag>
                            ))}
                          </TagWrap>
                        )}
                        </SearchArea>
                        <DisplayArea ref={displayAreaRef}>                          
                              {parents && 
                                <ResultsGrid2>
                                  {paginatedParents.map((parent: UserDetails) => (
                                    <div
                                      key={parent.id}
                                      onClick={() => handleCardClick(parent)}
                                      onMouseEnter={(e) => handleMouseEnter(e, parent)}
                                      onMouseLeave={handleMouseLeave}
                                    >
                                    <ParentCard parent={parent} tutor={tutorDetails!} />
                                    </div>
                                  ))}
                                </ResultsGrid2>
                            }
                          
                          {tutorDetails && hoveredParent && (
                            <SimilaritySidebar 
                              tutor={tutorDetails} 
                              parent={hoveredParent} 
                              isOpen={isSidebarOpen}
                              topPosition={sidebarTop}
                              onOverflowChange={setIsSidebarOverflowing} // The child will call this to update our state
                              dataState={sidebarFinalState} // Pass the final calculated state down
                              isParentUser={false}
                            />
                            )}

                        </DisplayArea>
                
                       <PageSelectArea>
                          <InfoBanner2 style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                          {/* Left arrow (or placeholder to keep spacing) */}
                          <BannerIconButton
                            onClick={canPrev ? handlePrevPage : undefined}
                            disabled={!canPrev}
                            aria-hidden={!canPrev}
                            tabIndex={canPrev ? 0 : -1}
                            style={{ visibility: canPrev ? 'visible' : 'hidden' }}
                          >
                            <ArrowLeftCircle size={18} />
                          </BannerIconButton>

                          <span style={{ fontWeight: 600 }}>
                            Page {displayCurrent} of {totalPages}
                          </span>

                          {/* Right arrow (or placeholder) */}
                          <BannerIconButton
                            onClick={canNext ? handleNextPage : undefined}
                            disabled={!canNext}
                            aria-hidden={!canNext}
                            tabIndex={canNext ? 0 : -1}
                            style={{ visibility: canNext ? 'visible' : 'hidden' }}
                          >
                            <ArrowRightCircle size={18} />
                          </BannerIconButton>
                          </InfoBanner2>
                          </PageSelectArea>

                          
                        </ContentArea>
                      </SearchPageShell2>
                    </>
                    {expandedParent && createPortal(
                      <Overlay onClick={closeProfileModal} style={{ zIndex: 9999 }}>
                        {/* Stop propagation so clicking the card doesn't close the modal */}
                        <Dialog onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%', height: '90vh', overflow: 'hidden', padding: 0, background: 'transparent', boxShadow: 'none', display: 'flex' }}>
                          
                            <ParentDetailView
                              parent={expandedParent}
                              tutor={tutorDetails!}
                              onClose={() => setExpandedParent(null)} 
                            />
                          
                          
                        </Dialog>
                      </Overlay>,
                      document.body
                    )}
                </>   
                </Area>
              )
            };

export default SearchPage;